import { Action, ActionReducer, createReducer, on } from '@ngrx/store';

import { nonNullish } from 'shared-utils/data';
import { getFileType } from 'shared-utils/file-system';

import { DpuFileType, isDpuFileType } from '../dpu-file.type';
import { SaveDpuFileActions } from './dpu-file.action';

export interface DpuFileState {
  saving: boolean;
  fileType: DpuFileType | undefined;
  currentDataOrSetupFilePath: string | undefined;
  currentSetupFilePath: string | undefined;
  appMetadata: { [key: string]: unknown } | undefined;
}

export const initialDpuFileState: DpuFileState = {
  saving: false,
  fileType: undefined,
  currentDataOrSetupFilePath: undefined,
  currentSetupFilePath: undefined,
  appMetadata: undefined,
};

const reducer: ActionReducer<DpuFileState> = createReducer(
  initialDpuFileState,

  on(SaveDpuFileActions.setPath, (state: DpuFileState, { path }: { path: string }): DpuFileState => {
    const fileType = getFileType(path);

    if (!isDpuFileType(fileType)) {
      throw new Error(`Unsupported DPU file type: "${fileType}"`);
    }

    return {
      ...initialDpuFileState,
      fileType,
      currentDataOrSetupFilePath: path,
      currentSetupFilePath: fileType === 'setup' ? path : initialDpuFileState.currentSetupFilePath,
    };
  }),

  on(SaveDpuFileActions.setAppMetadata, (state: DpuFileState, { appMetadata }: { appMetadata: object }): DpuFileState => ({
    ...state,
    appMetadata: {
      ...state.appMetadata,
      ...appMetadata,
    },
  })),

  on(SaveDpuFileActions.save, (state: DpuFileState, { fileType }): DpuFileState => ({
    ...state,
    saving: true,
    fileType,
  })),

  on(SaveDpuFileActions.saveSucceeded, (state: DpuFileState, { path, fileType }): DpuFileState => {
    const hasActiveDataContext: boolean = nonNullish(state.currentDataOrSetupFilePath)
      && getFileType(state.currentDataOrSetupFilePath) === 'data';

    return {
      ...state,
      saving: false,
      fileType: hasActiveDataContext && fileType === 'setup' ? 'data' : fileType,
      currentDataOrSetupFilePath: hasActiveDataContext && fileType === 'setup'
        ? state.currentDataOrSetupFilePath
        : path,
      currentSetupFilePath: fileType === 'setup' ? path : state.currentSetupFilePath,
    };
  }),

  on(SaveDpuFileActions.saveFailed, (state: DpuFileState): DpuFileState => ({
    ...state,
    saving: false,
  })),

  on(SaveDpuFileActions.restoreSetupInfo, (state: DpuFileState): DpuFileState => {
    if (state.currentSetupFilePath) {
      return {
        ...state,
        fileType: 'setup',
        currentDataOrSetupFilePath: state.currentSetupFilePath,
      };
    } else {
      return {
        ...state,
        fileType: initialDpuFileState.fileType,
        currentDataOrSetupFilePath: initialDpuFileState.currentDataOrSetupFilePath,
        currentSetupFilePath: initialDpuFileState.currentSetupFilePath,
      };
    }
  }),
);

export function dpuFileReducer(state: DpuFileState | undefined, action: Action): DpuFileState {
  return reducer(state, action);
}
